<script setup>
import logo from '@i/logo.svg';
import ImgMedia from '@/Components/Images/ImgMedia.vue';
import {useForm} from '@inertiajs/vue3';
import Form from '@/Components/Input/Form.vue';
import Button from '@/Components/Buttons/Button.vue';
import {inject, ref} from 'vue';
import DiscountDesktop from '@/../img/coming-soon/discount.svg?component';
import DiscountMobile from '@/../img/coming-soon/discount-mobile.svg?component';

/** @type import('ziggy-js').default */
const route = inject('route')

defineProps({
  image_mobile: Object,
  image_desktop: Object,
  background_image_mobile: Object,
  background_image_desktop: Object,
  products_image_mobile: Object,
  products_image_desktop: Object,
  text: String,
});

const formSubmitted = ref(false)

const form = useForm({
  name: '',
  email: '',
});

const submitForm = () => {
  form.post(route('api.pre-launch-form.store'), {
    onSuccess: () => {
      formSubmitted.value = true
    },
  });
};
</script>

<template>
    <div class="fixed inset-0 z-50 bg-red-300">
        <div class="" />
        <div class="absolute inset-0 size-full">
            <ImgMedia
                max-width="lg"
                eager
                :media="background_image_mobile"
                classes="object-cover h-full w-full absolute inset-0"
            />
            <ImgMedia
                min-width="lg"
                eager
                :media="background_image_desktop"
                classes="object-cover h-full w-full absolute inset-0"
            />
        </div>
        <div
            class="container hide-scrollbar flex h-full flex-col overflow-y-scroll py-8 text-center text-white md:py-16"
        >
            <img
                class="isolate mt-8 h-10"
                :src="logo"
                alt="First Nutrition logo"
            >
            <div class="relative my-8 flex items-center justify-center bg-black shadow-2xl md:mb-8 md:mt-15">
                <div class="absolute inset-0">
                    <ImgMedia
                        min-width="md"
                        eager
                        :media="image_desktop"
                        classes="md:h-full md:w-full object-cover hidden md:block"
                    />
                    <ImgMedia
                        max-width="md"
                        eager
                        :media="image_mobile"
                        classes="h-full w-full md:h-full md:w-unset object-cover md:hidden"
                    />
                </div>


                <div class="absolute -bottom-10 -left-10 lg:-bottom-17 lg:-left-27">
                    <ImgMedia
                        min-width="lg"
                        eager
                        :media="products_image_desktop"
                        classes="hidden lg:block"
                    />
                    <ImgMedia
                        max-width="lg"
                        eager
                        :media="products_image_mobile"
                        classes="lg:hidden"
                    />
                </div>

                <div class="absolute -bottom-10 -right-10 lg:-bottom-17 lg:-right-27">
                    <DiscountMobile class="lg:hidden" />

                    <DiscountDesktop class="hidden lg:block" />
                </div>

                <div class="isolate flex flex-col items-center justify-center gap-3 px-4 pb-28 pt-16 xl:pt-29">
                    <div class="mb-4 text-8 font-bold uppercase leading-10 md:text-12">
                        Pre-launch aanbieding
                    </div>

                    <div>
                        Meld je nu aan en ontvang direct jouw
                        unieke kortingscode bij de lancering van
                        onze webshop!:
                    </div>

                    <div class="w-full max-w-md justify-center">
                        <div v-if="formSubmitted">
                            <div class="mb-8 md:mb-16">
                                Formulier succesvol verzonden!
                            </div>
                        </div>
                        <Form v-else>
                            <FormKit
                                v-model="form.name"
                                type="text"
                                name="name"
                                placeholder="Jouw naam"
                                input-class="$reset block w-full px-4 py-2 text-sm bg-transparent border border-white rounded text-white focus:outline-none placeholder-white focus:ring-1 focus:ring-pink focus:border-pink  min-h-10 -my-[1px] disabled:bg-gray-medium"
                                autofocus="autofocus"
                            />
                            <FormKit
                                v-model="form.email"
                                type="email"
                                name="email"
                                placeholder="Jouw e-mailadres"
                                input-class="$reset block w-full px-4 py-2 text-sm bg-transparent border border-white rounded text-white focus:outline-none placeholder-white focus:ring-1 focus:ring-pink focus:border-pink  min-h-10 -my-[1px] disabled:bg-gray-medium"
                            />

                            <div class="flex items-center justify-center">
                                <Button
                                    type="submit"
                                    :label="'Aanmelden'"
                                    red
                                    @click="submitForm"
                                />
                            </div>
                        </Form>
                    </div>
                </div>
            </div>
            <div class="isolate pt-8 lg:pt-16">
                <div class="mb-4 text-8 font-bold uppercase leading-9 md:text-12">
                    Onze fysieke winkel is geopend!
                </div>
                <div class="mb-8 text-3 md:text-5">
                    <span>Ma t/m do 9:00 - 13:30 & 17:00 -20:00</span><br class="md:hidden"><span
                        class="hidden md:inline"
                    > | </span>
                    <span>Vr: 9:00 - 14:00</span><br class="md:hidden"><span class="hidden md:inline"> | </span>
                    <span>Za 09:00 -12:00</span><span class="hidden md:inline"> | </span>
                </div>

                <a
                    class="flex items-center justify-center gap-2 text-3 md:text-5"
                    href="https://goo.gl/maps/GApHN9PaThwhuY4o9"
                >
                    <svg
                        width="16"
                        height="20"
                        viewBox="0 0 16 20"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <path
                            d="M8.00222 9.75C8.48407 9.75 8.89583 9.57843 9.2375 9.23528C9.57917 8.89214 9.75 8.47964 9.75 7.99778C9.75 7.51593 9.57843 7.10417 9.23528 6.7625C8.89214 6.42083 8.47964 6.25 7.99778 6.25C7.51593 6.25 7.10417 6.42158 6.7625 6.76472C6.42083 7.10786 6.25 7.52036 6.25 8.00222C6.25 8.48407 6.42158 8.89583 6.76472 9.2375C7.10786 9.57917 7.52036 9.75 8.00222 9.75ZM8 20C5.31667 17.7167 3.3125 15.5958 1.9875 13.6375C0.6625 11.6792 0 9.86667 0 8.2C0 5.7 0.804167 3.70833 2.4125 2.225C4.02083 0.741667 5.88333 0 8 0C10.1167 0 11.9792 0.741667 13.5875 2.225C15.1958 3.70833 16 5.7 16 8.2C16 9.86667 15.3375 11.6792 14.0125 13.6375C12.6875 15.5958 10.6833 17.7167 8 20Z"
                            fill="white"
                        />
                    </svg>

                    Industrieweg 56, 3641 RL Mijdrecht</a>
                <a
                    class="block text-center text-xl font-bold text-white underline"
                    href="mailto:info@firstnutrition.nl"
                >info@firstnutrition.nl</a>
            </div>
        </div>
    </div>
</template>
